import { createSlice } from "@reduxjs/toolkit"

import { logout } from "../../containers/LoginPage/redux/auth.thunks.ts"
import { notificationCountsSliceName } from "./notification-counts.constants.ts"
import {
  calendarNotificationsCleared,
  fetchNotificationCounts,
  fetchPendingApprovals,
  fetchSubcontractorComplianceCount,
  homeApprovalsTaken,
  projectNotificationsCleared
} from "./notification-counts.thunks.ts"
import type { NotificationCountsState } from "./notification-counts.types.ts"

const initialState: NotificationCountsState = {
  projects: 0,
  projectItems: [],
  calendar: 0,
  calendarItems: [],
  approvals: {
    proposals: 0,
    proposalItems: [],
    contracts: 0,
    contractItems: [],
    documents: 0,
    total: 0
  },
  subcontractors: 0,
  requestId: null,
  status: "idle",
  approvalRows: null,
  approvalsRequest: null
}

const notificationCountsSlice = createSlice({
  name: notificationCountsSliceName,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(logout.fulfilled, () => initialState)
      .addCase(fetchNotificationCounts.pending, (state, action) => {
        state.status = "loading"
        state.requestId = action.meta.requestId
        if (action.meta.readsApprovals === false) return
        state.approvalsRequest = {
          requestId: action.meta.requestId,
          team: action.meta.team ?? null,
          at: action.meta.at,
          status: "loading",
          offered: true
        }
      })
      .addCase(fetchNotificationCounts.fulfilled, (state, action) => {
        if (state.approvalsRequest?.requestId === action.meta.requestId) {
          state.approvalsRequest.status = "done"
          if (action.payload.approvals)
            state.approvals = action.payload.approvals
          state.approvalRows = action.payload.approvalRows ?? null
        }
        if (state.requestId !== action.meta.requestId) return
        state.status = "succeeded"
        state.requestId = null
        state.projects = action.payload.projects
        state.projectItems = action.payload.projectItems
        state.calendar = action.payload.calendar
        state.calendarItems = action.payload.calendarItems
        state.subcontractors = action.payload.subcontractors
      })
      .addCase(fetchPendingApprovals.pending, (state, action) => {
        state.approvalsRequest = {
          requestId: action.meta.requestId,
          team: action.meta.team ?? null,
          at: action.meta.at,
          status: "loading",
          offered: false
        }
      })
      .addCase(fetchPendingApprovals.fulfilled, (state, action) => {
        if (state.approvalsRequest?.requestId !== action.meta.requestId) return
        state.approvalsRequest.status = "done"
        if (action.payload.approvals) state.approvals = action.payload.approvals
        state.approvalRows = action.payload.approvalRows ?? null
      })
      .addCase(homeApprovalsTaken, (state) => {
        if (state.approvalsRequest) state.approvalsRequest.offered = false
      })
      .addCase(fetchPendingApprovals.rejected, (state, action) => {
        if (state.approvalsRequest?.requestId === action.meta.requestId)
          state.approvalsRequest.status = "done"
      })
      .addCase(fetchNotificationCounts.rejected, (state, action) => {
        if (state.approvalsRequest?.requestId === action.meta.requestId)
          state.approvalsRequest.status = "done"
        if (state.requestId !== action.meta.requestId) return
        state.status = "failed"
        state.requestId = null
      })
      .addCase(fetchSubcontractorComplianceCount.fulfilled, (state, action) => {
        if (action.payload !== null) state.subcontractors = action.payload
      })
      .addCase(projectNotificationsCleared, (state) => {
        state.projects = 0
        state.projectItems = []
      })
      .addCase(calendarNotificationsCleared, (state) => {
        state.calendar = 0
        state.calendarItems = []
      })
  }
})

export default notificationCountsSlice
